import React, { useContext, useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import { AlertInfo } from "components/AlertsManagement/AlertInfo/AlertInfo";
import { DismissAlertDialog } from "components/AlertsManagement/DismissAlertDialog";
import { Snackbar } from "components/Snackbar";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useGetAlerts } from "hooks/alerts/useGetAlerts";
import { useTrackPageView } from "hooks/useTrackPageView";
import { nth, size, difference, head } from "lodash";
import { FixedHeightPaper } from "components/AlertsManagement/FixedHeightPaper";
import { AlertTableControls } from "components/AlertsManagement/AlertsTable/AlertsTableControls";
import { usePagination } from "hooks/table-hooks/usePagination";
import { Typography } from "@mui/material";
import { getTimeFromDate } from "core/utils/getTimeFromDate";
import { AlertFiltersContext } from "contexts/AlertFiltersContext";
import { AlertsTable } from "components/AlertsManagement/AlertsTable/AlertsTable";
import { LinkButton } from "components/LinkButton";
import { styled } from "@mui/material/styles";

const PAGE_TITLE = "Alerts";

export const AlertsPage = () => {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);

  const [alert, setAlert] = useState();
  const [hasInitialAlert, setHasInitialAlert] = useState(false);
  const [alertsToDismiss, setAlertsToDismiss] = useState();
  const [openDismissConfirmation, setOpenDismissConfirmation] = useState(false);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [sortByFields, setSortByFields] = useState([]);

  const {
    pageIndex,
    pageSize,
    changePageIndex,
    changePageSize
  } = usePagination();

  const { filters, setFilters } = useContext(AlertFiltersContext);

  const {
    severityFilter,
    siteFilter,
    machineFilter,
    startDateFilter,
    endDateFilter
  } = filters;

  const {
    addSnackbarToQueue,
    handleOnCloseSnackBar,
    activeSnackbar,
    open: openSnackbar
  } = useContext(SnackbarQueueContext);

  const {
    loading: alertsLoading,
    error,
    alerts,
    count,
    refetchAlerts
  } = useGetAlerts(
    pageIndex + 1,
    pageSize,
    severityFilter.length !== 0 ? severityFilter : undefined,
    siteFilter?.id,
    machineFilter?.id,
    getTimeFromDate(startDateFilter),
    getTimeFromDate(endDateFilter),
    sortByFields
  );

  const selectedOnCurrentPage = useMemo(() => {
    // when the alerts change, create a list of alerts that are selected on the current page
    if (alertsLoading && error) {
      return null;
    }

    return alerts.filter(alert =>
      selectedAlerts.some(
        selectedAlert => alert.alert_id === selectedAlert.alert_id
      )
    );
  }, [alerts, alertsLoading, error, selectedAlerts]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (
        !openDismissConfirmation &&
        selectedOnCurrentPage &&
        event.key === "Delete" &&
        !event.target.matches("input")
      ) {
        setAlertsToDismiss(selectedOnCurrentPage);
        setOpenDismissConfirmation(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown, false);

    return () => document.removeEventListener("keydown", handleKeyDown, false);
  }, [selectedOnCurrentPage, openDismissConfirmation]);

  useEffect(() => {
    if (size(alerts) > 0 && !hasInitialAlert) {
      setSelectedAlerts([nth(alerts, 0)]);
      setAlert(nth(alerts, 0));
      setHasInitialAlert(true);
    }
  }, [hasInitialAlert, alerts, pageIndex]);

  const handleSelectAlerts = (newSelectedAlerts, activeAlert) => {
    setSelectedAlerts(newSelectedAlerts);
    setAlert(activeAlert);
  };

  const handleDismissAlerts = alertsToDismiss => {
    setAlertsToDismiss(alertsToDismiss);
    setOpenDismissConfirmation(true);
  };

  const handleAlertsActioned = actionedAlerts => {
    // when the alerts actioned on the current page is equal to the number of the alerts displayed
    // we need to change to show the previous page
    if (actionedAlerts.length >= alerts.length) {
      let newIndex = pageIndex - 1;

      if (newIndex >= 0) {
        // show the previous page
        changePageIndex(newIndex);
      } else if (pageIndex === 0) {
        // we can't go lower than 0, so refresh the current page
        refetchAlerts();
      } else {
        // if the new index would be lower than 0, instead set the page to 0
        changePageIndex(0);
      }
    } else {
      // if the number of alerts actioned means that the same page would be displayed
      // we should instead refresh the current page
      refetchAlerts();
    }

    // remove the actioned alerts from the current selected alerts
    setSelectedAlerts(prevSelected => difference(prevSelected, actionedAlerts));
    // Gets the currently selected alerts minus the ones actioned (saves a re-render)
    const newSelectedAlertsOnPage = difference(
      selectedOnCurrentPage,
      actionedAlerts
    );
    setAlert(head(newSelectedAlertsOnPage));
  };

  const handleDismissSuccess = () => {
    setOpenDismissConfirmation(false);
    addSnackbarToQueue({
      severity: "success",
      message:
        alertsToDismiss.length > 1
          ? `${alertsToDismiss.length} Alerts Dismissed Successfully.`
          : `Alert Dismissed Successfully.`
    });
    handleAlertsActioned(alertsToDismiss);
  };

  return (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
    >
      <Grid item xs={12} sm={12} md={12} lg={7}>
        <FixedHeightPaper>
          <Typography variant="h4">Alerts</Typography>
          <AlertTableControls
            filters={filters}
            onFilterChange={filterChange => {
              changePageIndex(0);
              setFilters(filterChange);
            }}
          />
          <AlertsTable
            loading={alertsLoading}
            alertsTableData={alerts ?? []}
            selectedOnCurrentPage={selectedOnCurrentPage}
            count={count}
            pageIndex={pageIndex}
            pageSize={pageSize}
            selectedAlerts={selectedAlerts}
            onSelectAlerts={handleSelectAlerts}
            onPageChange={(_event, pageIndex) => {
              changePageIndex(pageIndex);
            }}
            onSortingChange={sortBy => {
              changePageIndex(0);
              setSortByFields(sortBy);
            }}
            onUpdatePageSize={changePageSize}
            onClickDismissAlerts={handleDismissAlerts}
          />
          <BottomSection>
            <LinkButton
              variant="outlined"
              color="primary"
              data-qa="AP-Button-View-Archive"
              to="/pages/alerts/archived"
            >
              View archive
            </LinkButton>
            <LinkButton
              variant="outlined"
              color="primary"
              to="/pages/alerts/rule-management"
            >
              Manage Rules
            </LinkButton>
          </BottomSection>
        </FixedHeightPaper>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={5}>
        <AlertInfo
          alert={alert}
          onCreateCaseComplete={() => handleAlertsActioned([alert])}
          onClickDismissAlert={handleDismissAlerts}
          alertsLoading={alertsLoading}
          alertsIsEmpty={alerts.length === 0}
        />
      </Grid>
      {openDismissConfirmation && (
        <DismissAlertDialog
          open={openDismissConfirmation}
          alertsToDismiss={alertsToDismiss}
          onDismissSuccess={handleDismissSuccess}
          onClose={() => {
            setOpenDismissConfirmation(false);
            setAlertsToDismiss();
          }}
        />
      )}

      <Snackbar
        open={openSnackbar}
        onClose={handleOnCloseSnackBar}
        {...activeSnackbar}
      >
        {activeSnackbar?.message ?? ""}
      </Snackbar>
    </Grid>
  );
};

const BottomSection = styled("div")`
  display: flex;
  flex-direction: row;
  padding-top: ${props => props.theme.spacing(1)};
  gap: ${props => props.theme.spacing(1)};
`;
